import { useCallback } from 'react';
import type { CanvasObject } from '../types';
import type { Artboard } from './useCanvasTransform';
import { getRotatedAABB, calculateAlignBounds } from '../utils/alignUtils';

export type AlignMode = 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom' | 'distribute-h' | 'distribute-v';

interface UseAlignProps {
  selectedObjects: CanvasObject[];
  updateObject: (id: string, updater: (obj: CanvasObject) => CanvasObject) => void;
  requestRender: () => void;
  saveToHistory: (state: { objects: Map<string, CanvasObject>; zOrder: string[]; selectedId: string | null }) => void;
  objects: Map<string, CanvasObject>;
  zOrder: string[];
  selectedId: string | null;
  artboardsMap: Map<string, Artboard>;
}

export function useAlign({
  selectedObjects,
  updateObject,
  requestRender,
  saveToHistory,
  objects,
  zOrder,
  selectedId,
  artboardsMap
}: UseAlignProps) {
  const align = useCallback((mode: AlignMode) => {
    if (selectedObjects.length === 0) return;
    
    // 균등 배치 처리
    if (mode === 'distribute-h' || mode === 'distribute-v') {
      if (selectedObjects.length < 2) return;
      
      const objectsWithAABB = selectedObjects.map(obj => ({
        obj,
        aabb: getRotatedAABB(obj)
      }));
      
      if (mode === 'distribute-h') {
        // 가로 균등 배치: x 좌표 기준으로 정렬
        objectsWithAABB.sort((a, b) => a.aabb.centerX - b.aabb.centerX);
      } else {
        // 세로 균등 배치: y 좌표 기준으로 정렬
        objectsWithAABB.sort((a, b) => a.aabb.centerY - b.aabb.centerY);
      }
      
      const first = objectsWithAABB[0];
      const last = objectsWithAABB[objectsWithAABB.length - 1];
      
      if (mode === 'distribute-h') {
        const startPos = first.aabb.centerX;
        const endPos = last.aabb.centerX;
        const totalGap = endPos - startPos;
        const gap = objectsWithAABB.length > 2 ? totalGap / (objectsWithAABB.length - 1) : 0;
        
        objectsWithAABB.forEach((item, index) => {
          if (index === 0 || index === objectsWithAABB.length - 1) return;
          
          const targetCenterX = startPos + gap * index;
          const currentCenterX = item.aabb.centerX;
          const dx = targetCenterX - currentCenterX;
          
          updateObject(item.obj.id, (o) => ({ ...o, x: Math.round(o.x + dx) }));
        });
      } else {
        const startPos = first.aabb.centerY;
        const endPos = last.aabb.centerY;
        const totalGap = endPos - startPos;
        const gap = objectsWithAABB.length > 2 ? totalGap / (objectsWithAABB.length - 1) : 0;
        
        objectsWithAABB.forEach((item, index) => {
          if (index === 0 || index === objectsWithAABB.length - 1) return;
          
          const targetCenterY = startPos + gap * index;
          const currentCenterY = item.aabb.centerY;
          const dy = targetCenterY - currentCenterY;
          
          updateObject(item.obj.id, (o) => ({ ...o, y: Math.round(o.y + dy) }));
        });
      }
      
      requestRender();
      
      // 히스토리 저장
      saveToHistory({
        objects: new Map(objects),
        zOrder: [...zOrder],
        selectedId
      });
      
      return;
    }
    
    // 일반 정렬 처리
    const bounds = calculateAlignBounds(selectedObjects, artboardsMap);
    
    const targetLeft = bounds.x;
    const targetRight = bounds.x + bounds.width;
    const targetTop = bounds.y;
    const targetBottom = bounds.y + bounds.height;
    const targetCX = bounds.x + bounds.width / 2;
    const targetCY = bounds.y + bounds.height / 2;
    
    selectedObjects.forEach(obj => {
      const b = getRotatedAABB(obj);
      let dx = 0, dy = 0;
      switch (mode) {
        case 'left':
          dx = targetLeft - b.minX;
          break;
        case 'right':
          dx = targetRight - b.maxX;
          break;
        case 'hcenter': {
          const cx = (b.minX + b.maxX) / 2;
          dx = targetCX - cx;
          break;
        }
        case 'top':
          dy = targetTop - b.minY;
          break;
        case 'bottom':
          dy = targetBottom - b.maxY;
          break;
        case 'vcenter': {
          const cy = (b.minY + b.maxY) / 2;
          dy = targetCY - cy;
          break;
        }
      }
      updateObject(obj.id, (o) => ({ 
        ...o, 
        x: Math.round(o.x + dx), 
        y: Math.round(o.y + dy) 
      }));
    });
    
    requestRender();
    
    // 히스토리 저장
    saveToHistory({
      objects: new Map(objects),
      zOrder: [...zOrder],
      selectedId
    });
  }, [selectedObjects, updateObject, requestRender, saveToHistory, objects, zOrder, selectedId, artboardsMap]);
  
  return { align };
}

